<template>
  <div class="sf-el address address-input">
    <div class="sf-address">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>

      <!-- COMPACT VIEW: Only postcode + huisnummer (for smart mode) -->
      <div v-if="viewState === 'compact'" class="address__compact">
        <div class="address__compact-row">
          <div class="address__field address__field--postal-compact">
            <input
              type="text"
              v-model="postalCodeQuery"
              autocomplete="postal-code"
              :placeholder="$t('form.fields.address.postal_code')"
              class="address__input"
              @input="handleCompactPostalInput"
              @blur="handleCompactBlur"
            />
          </div>
          <div class="address__field address__field--number-compact">
            <input
              type="text"
              v-model="addressData.houseNumber"
              autocomplete="address-line2"
              :placeholder="$t('form.fields.address.house_number')"
              class="address__input"
              @input="handleCompactHouseNumberInput"
              @blur="handleCompactBlur"
            />
          </div>
        </div>
        <div v-if="isLoading" class="address__compact-loading">
          <div class="address__autocomplete-spinner"></div>
          <span>{{ $t('form.fields.address.searching') || 'Adres zoeken...' }}</span>
        </div>
        <div v-if="lookupError" class="address__error">
          {{ lookupError }}
        </div>
      </div>

      <!-- CONFIRMED VIEW: Show result with edit button (for smart mode) -->
      <div v-else-if="viewState === 'confirmed'" class="address__confirmed">
        <div class="address__preview">
          <div class="address__preview-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.5 4.5L6.5 11.5L3 8"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="address__preview-text">
            <div class="address__preview-line1">
              {{ addressData.street }} {{ addressData.houseNumber }}
            </div>
            <div class="address__preview-line2">
              {{ addressData.postalCode }} {{ addressData.city }}
            </div>
          </div>
          <button type="button" @click="expandForm" class="address__edit-btn">
            {{ $t('form.fields.address.edit') || 'Wijzigen' }}
          </button>
        </div>
      </div>

      <!-- EXPANDED/FULL VIEW: All fields -->
      <div v-else class="address__fields">
        <div class="address__field-row address__field-row--no-break">
          <div class="address__field address__field--street">
            <input
              ref="streetInput"
              type="text"
              v-model="addressQuery"
              :placeholder="$t('form.fields.address.street')"
              class="address__input"
              @input="handleStreetInput"
              @focus="
                activeSearchField = 'street';
                showAutocomplete = true;
              "
              @blur="handleStreetBlur"
              autocomplete="address-line1"
            />
            <!-- Autocomplete dropdown - only show when street field is active -->
            <div
              v-if="
                activeSearchField === 'street' &&
                showAutocomplete &&
                (autocompleteResults.length > 0 || isLoading)
              "
              class="address__autocomplete"
            >
              <!-- Loading state -->
              <div v-if="isLoading" class="address__autocomplete-loading">
                <div class="address__autocomplete-spinner"></div>
                <span>Zoeken...</span>
              </div>

              <!-- Results -->
              <div v-else-if="autocompleteResults.length > 0">
                <div
                  v-for="(result, index) in autocompleteResults"
                  :key="index"
                  class="address__autocomplete-item"
                  @mousedown.prevent="selectAutocompleteResult(result)"
                >
                  <div class="address__autocomplete-name">{{ result.text }}</div>
                  <div v-if="result.description" class="address__autocomplete-address">
                    {{ result.description }}
                  </div>
                </div>
              </div>

              <!-- No results -->
              <div v-else-if="addressQuery.length >= 2" class="address__autocomplete-no-results">
                Geen adressen gevonden voor "{{ addressQuery }}"
              </div>
            </div>
          </div>
          <div class="address__field address__field--number">
            <input
              type="text"
              v-model="addressData.houseNumber"
              autocomplete="address-line2"
              :placeholder="$t('form.fields.address.house_number')"
              class="address__input"
            />
          </div>
        </div>
        <div class="address__field-row address__field-row--no-break">
          <div class="address__field address__field--postal">
            <input
              type="text"
              v-model="postalCodeQuery"
              autocomplete="postal-code"
              :placeholder="$t('form.fields.address.postal_code')"
              class="address__input"
              @input="handlePostalCodeInput"
              @focus="
                activeSearchField = 'postalCode';
                showAutocomplete = true;
              "
              @blur="handlePostalCodeBlur"
            />
            <!-- Autocomplete dropdown - only show when postal code field is active -->
            <div
              v-if="
                activeSearchField === 'postalCode' &&
                showAutocomplete &&
                (autocompleteResults.length > 0 || isLoading)
              "
              class="address__autocomplete"
            >
              <!-- Loading state -->
              <div v-if="isLoading" class="address__autocomplete-loading">
                <div class="address__autocomplete-spinner"></div>
                <span>Zoeken...</span>
              </div>

              <!-- Results -->
              <div v-else-if="autocompleteResults.length > 0">
                <div
                  v-for="(result, index) in autocompleteResults"
                  :key="index"
                  class="address__autocomplete-item"
                  @mousedown.prevent="selectAutocompleteResult(result)"
                >
                  <div class="address__autocomplete-name">{{ result.text }}</div>
                  <div v-if="result.description" class="address__autocomplete-address">
                    {{ result.description }}
                  </div>
                </div>
              </div>

              <!-- No results -->
              <div v-else-if="postalCodeQuery.length >= 2" class="address__autocomplete-no-results">
                Geen adressen gevonden voor "{{ postalCodeQuery }}"
              </div>
            </div>
          </div>
          <div class="address__field address__field--city">
            <input
              type="text"
              v-model="addressData.city"
              autocomplete="address-level2"
              :placeholder="$t('form.fields.address.city')"
              class="address__input"
            />
          </div>

          <div class="address__field address__field--country">
            <select
              v-model="addressData.country"
              autocomplete="country"
              class="address__input"
              placeholder="Select country"
            >
              <option value="" class="placeholder" disabled>
                {{ $t('form.fields.address.select_country') }}
              </option>
              <option v-for="country in countries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <Btn
          v-if="showAddressCheck && enablePdokLookup"
          type="button"
          class="address__check-btn"
          variant="primary"
          size="sm"
          :text="$t('form.fields.address.check_address') || 'Bevestig'"
          :loading="isCheckingAddress"
          :disabled="isCheckingAddress"
          @click="checkExpandedAddress"
        />
        <div v-if="lookupError" class="address__error">
          {{ lookupError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { watch, ref, onMounted, onUnmounted, nextTick, computed, getCurrentInstance } from 'vue';
  import { COUNTRIES } from '@shared/constants/countries';
  import Btn from '@shared/components/Btn.vue';

  export default {
    name: 'AddressElement',
    components: { Btn },
    props: {
      label: String,
      description: String,
      name: String,
      modelValue: {
        type: Object,
        default: () => ({
          houseNumber: '',
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          placeId: '',
        }),
      },
      required: Boolean,
      // Display mode: 'full' (all fields), 'compact' (postcode + huisnummer only), 'smart' (compact with auto-expand)
      mode: {
        type: String,
        default: 'smart', // Default to smart mode for better UX
        validator: (value) => ['full', 'compact', 'smart'].includes(value),
      },
      // Enable Google Places Autocomplete
      enableAutocomplete: {
        type: Boolean,
        default: true,
      },
      // Google Maps API key (or use environment variable)
      googleMapsApiKey: {
        type: String,
        default: () => {
          // Try to get from environment or runtime config
          if (typeof window !== 'undefined') {
            return (
              window.__NUXT__?.config?.public?.googleMapsApiKey ||
              import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
              ''
            );
          }
          return '';
        },
      },
      // Enable PDOK lookup for Dutch addresses (postcode + huisnummer)
      enablePdokLookup: {
        type: Boolean,
        default: true,
      },
      showAddressCheck: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'view-state'],
    async setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance?.appContext?.config?.globalProperties?.$t;
      const addressData = ref({ ...props.modelValue });
      const streetInput = ref(null);
      const lookupError = ref('');
      const addressQuery = ref(props.modelValue.street || '');
      const postalCodeQuery = ref(props.modelValue.postalCode || '');
      const showAutocomplete = ref(false);
      const autocompleteResults = ref([]);
      const searchTimeout = ref(null);
      const lookupTimeout = ref(null);
      let smartLookupId = 0;
      let expandTimer = null;
      const isLoading = ref(false);
      const isCheckingAddress = ref(false);
      const activeSearchField = ref(null);

      // View state for smart mode: 'compact' | 'confirmed' | 'expanded'
      const getInitialViewState = () => {
        // Default to smart mode if not specified
        const mode = props.mode || 'smart';

        if (mode === 'full') return 'expanded';
        if (mode === 'compact') return 'compact';
        // Smart mode: check if we already have a complete address
        if (mode === 'smart') {
          const hasCompleteAddress =
            props.modelValue?.street &&
            props.modelValue?.houseNumber &&
            props.modelValue?.postalCode &&
            props.modelValue?.city;
          return hasCompleteAddress ? 'confirmed' : 'compact';
        }
        return 'expanded';
      };

      const viewState = ref(getInitialViewState());

      watch(
        viewState,
        (state) => {
          emit('view-state', state);
        },
        { immediate: true }
      );

      if (!addressData.value.country) {
        addressData.value.country = '';
      }

      let abortController = null;

      /**
       * Expand to full form view
       */
      const addressSnapshot = ref(null);

      const addressKey = () => {
        const country = (addressData.value.country || '').trim().toUpperCase();
        const normalizedCountry =
          !country || country === 'NL' || country === 'NEDERLAND' ? 'NL' : country;
        return {
          street: (addressData.value.street || '').trim(),
          city: (addressData.value.city || '').trim(),
          postalCode: (addressData.value.postalCode || '').replace(/\s+/g, '').toUpperCase(),
          houseNumber: (addressData.value.houseNumber || '').trim(),
          country: normalizedCountry,
        };
      };

      const expandForm = () => {
        addressSnapshot.value = addressKey();
        viewState.value = 'expanded';
      };

      const isUnchangedExpandedAddress = () => {
        const snapshot = addressSnapshot.value;
        if (!snapshot) return false;
        if (!snapshot.street || !snapshot.city || !snapshot.postalCode || !snapshot.houseNumber) {
          return false;
        }
        const current = addressKey();
        return (
          current.street === snapshot.street &&
          current.city === snapshot.city &&
          current.postalCode === snapshot.postalCode &&
          current.houseNumber === snapshot.houseNumber &&
          current.country === snapshot.country
        );
      };

      /**
       * Handle compact mode postal code input
       */
      const handleCompactPostalInput = (event) => {
        const value = event.target.value;
        postalCodeQuery.value = value;
        addressData.value.postalCode = value;
        lookupError.value = '';

        // Try auto-lookup if we have both fields
        trySmartLookup();
      };

      /**
       * Handle compact mode house number input
       */
      const handleCompactHouseNumberInput = (event) => {
        const value = event.target.value;
        addressData.value.houseNumber = value;
        lookupError.value = '';

        // Try auto-lookup if we have both fields
        trySmartLookup();
      };

      /**
       * Show an inline error when the postal code is filled in but incomplete
       * (e.g. letters missing), so users notice before clicking "next".
       * Only enforced for Dutch addresses (empty country defaults to NL).
       */
      const validatePostalCodeFormat = () => {
        const country = (addressData.value.country || 'NL').toUpperCase();
        if (country !== 'NL') return;

        const cleanPostalCode = (addressData.value.postalCode || '').replace(/\s+/g, '');
        if (cleanPostalCode && !/^\d{4}[A-Za-z]{2}$/.test(cleanPostalCode)) {
          lookupError.value =
            $t?.('form.fields.address.postal_code_invalid') ||
            'Vul een geldige postcode in (bijv. 1234 AB)';
        }
      };

      /**
       * Handle blur in compact mode - trigger lookup if not already done
       */
      const handleCompactBlur = () => {
        // Small delay to allow for tab between fields
        setTimeout(() => {
          trySmartLookup();
          validatePostalCodeFormat();
        }, 100);
      };

      /**
       * Try smart lookup when both postal code and house number are filled
       */
      const trySmartLookup = () => {
        if (lookupTimeout.value) {
          clearTimeout(lookupTimeout.value);
        }

        const postalCode = addressData.value.postalCode;
        const houseNumber = addressData.value.houseNumber;

        // Validate Dutch postal code format
        const cleanPostalCode = postalCode?.replace(/\s+/g, '').toUpperCase() || '';
        const isValidPostalCode =
          cleanPostalCode.length === 6 && /^\d{4}[A-Z]{2}$/i.test(cleanPostalCode);

        if (isValidPostalCode && houseNumber?.trim()) {
          // Debounce the lookup
          lookupTimeout.value = setTimeout(async () => {
            await lookupPdokAddressForSmart(cleanPostalCode, houseNumber.trim());
          }, 500);
        }
      };

      /**
       * PDOK lookup specifically for smart mode - transitions to confirmed or expanded
       */
      const scheduleExpand = (delay) => {
        if (expandTimer) {
          clearTimeout(expandTimer);
        }
        expandTimer = setTimeout(() => {
          expandTimer = null;
          if (viewState.value === 'compact') {
            viewState.value = 'expanded';
          }
        }, delay);
      };

      const lookupPdokAddressForSmart = async (postalCode, houseNumber) => {
        const requestId = ++smartLookupId;
        if (expandTimer) {
          clearTimeout(expandTimer);
          expandTimer = null;
        }

        if (!props.enablePdokLookup) {
          // No PDOK, fall back to expanded view
          viewState.value = 'expanded';
          return;
        }

        isLoading.value = true;
        lookupError.value = '';

        try {
          const lookupUrl = '/api/address/lookup';
          const response = await fetch(
            `${lookupUrl}?postalCode=${encodeURIComponent(postalCode)}&houseNumber=${encodeURIComponent(houseNumber)}&country=NL`
          );

          if (requestId !== smartLookupId) return;

          if (!response.ok) {
            throw new Error('Address lookup request failed');
          }

          const result = await response.json();

          if (requestId !== smartLookupId) return;

          if (result.success && result.data) {
            // Update address data
            addressData.value.street = result.data.street || '';
            addressQuery.value = result.data.street || '';
            addressData.value.city = result.data.city || '';
            addressData.value.postalCode = result.data.postalCode || postalCode;
            postalCodeQuery.value = result.data.postalCode || postalCode;
            addressData.value.houseNumber = result.data.houseNumber || houseNumber;
            addressData.value.country = result.data.country || 'NL';
            addressData.value.coordinates = result.data.coordinates || null;

            // Transition to confirmed view
            viewState.value = 'confirmed';
          } else {
            // Lookup returned no results - show error or expand
            lookupError.value =
              result.error || 'Adres niet gevonden. Controleer postcode en huisnummer.';
            // After a short delay, expand to full form so user can enter manually
            scheduleExpand(2000);
          }
        } catch (error) {
          if (requestId !== smartLookupId) return;
          console.warn('Smart address lookup failed:', error);
          lookupError.value = 'Adres opzoeken mislukt. Vul handmatig in.';
          // Expand to full form on error
          scheduleExpand(1500);
        } finally {
          if (requestId === smartLookupId) {
            isLoading.value = false;
          }
        }
      };

      /**
       * Search addresses using Places API (New) Autocomplete endpoint
       */
      const searchPlaces = async (query) => {
        if (!props.enableAutocomplete || !query || query.length < 3) {
          autocompleteResults.value = [];
          return;
        }

        const apiKey =
          props.googleMapsApiKey ||
          (typeof window !== 'undefined' && window.__NUXT__?.config?.public?.googleMapsApiKey) ||
          '';

        if (!apiKey) {
          return;
        }

        if (abortController) {
          abortController.abort();
        }

        abortController = new AbortController();

        try {
          const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': apiKey,
              'X-Goog-FieldMask':
                'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
            },
            body: JSON.stringify({
              input: query,
              languageCode: 'nl',
              regionCode: addressData.value.country || 'NL',
            }),
            signal: abortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Places API error: ${response.status}`);
          }

          const data = await response.json();

          if (data.suggestions && data.suggestions.length > 0) {
            autocompleteResults.value = data.suggestions
              .filter((suggestion) => suggestion.placePrediction)
              .map((suggestion) => ({
                placeId: suggestion.placePrediction.placeId,
                text:
                  suggestion.placePrediction.text.structuredFormat?.mainText?.text ||
                  suggestion.placePrediction.text.text,
                description:
                  suggestion.placePrediction.text.structuredFormat?.secondaryText?.text || '',
              }));
          } else {
            autocompleteResults.value = [];
          }
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }
          console.warn('Places API autocomplete failed:', error);
          autocompleteResults.value = [];
        }
      };

      /**
       * Handle street input - debounced search
       */
      const handleStreetInput = (event) => {
        const query = event.target.value;
        addressQuery.value = query;
        addressData.value.street = query;
        activeSearchField.value = 'street';

        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }

        if (query.length < 2) {
          autocompleteResults.value = [];
          showAutocomplete.value = false;
          isLoading.value = false;
          return;
        }

        showAutocomplete.value = true;

        searchTimeout.value = setTimeout(() => {
          performSearch(query);
        }, 200);
      };

      /**
       * Handle postal code input - debounced search
       */
      const handlePostalCodeInput = (event) => {
        const query = event.target.value;
        postalCodeQuery.value = query;
        addressData.value.postalCode = query;
        activeSearchField.value = 'postalCode';
        lookupError.value = '';

        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }

        if (query.length < 2) {
          autocompleteResults.value = [];
          showAutocomplete.value = false;
          isLoading.value = false;
          return;
        }

        showAutocomplete.value = true;

        searchTimeout.value = setTimeout(() => {
          performSearch(query);
        }, 200);
      };

      /**
       * Perform the autocomplete search
       */
      const performSearch = async (query) => {
        if (query.length < 2) {
          autocompleteResults.value = [];
          isLoading.value = false;
          return;
        }

        isLoading.value = true;
        lookupError.value = '';

        try {
          await searchPlaces(query);
        } catch (error) {
          console.warn('Autocomplete search failed:', error);
          lookupError.value = 'Zoeken mislukt. Probeer het opnieuw.';
          autocompleteResults.value = [];
        } finally {
          isLoading.value = false;
        }
      };

      /**
       * Get place details by placeId
       */
      const getPlaceDetails = async (placeId) => {
        const apiKey =
          props.googleMapsApiKey ||
          (typeof window !== 'undefined' && window.__NUXT__?.config?.public?.googleMapsApiKey) ||
          '';

        if (!apiKey) {
          return null;
        }

        try {
          const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
            method: 'GET',
            headers: {
              'X-Goog-Api-Key': apiKey,
              'X-Goog-FieldMask': 'formattedAddress,location,addressComponents',
            },
          });

          if (!response.ok) {
            throw new Error(`Places API error: ${response.status}`);
          }

          return await response.json();
        } catch (error) {
          console.warn('Failed to get place details:', error);
          return null;
        }
      };

      /**
       * Select an autocomplete result
       */
      const selectAutocompleteResult = async (result) => {
        showAutocomplete.value = false;
        autocompleteResults.value = [];
        lookupError.value = '';

        const placeDetails = await getPlaceDetails(result.placeId);

        // Store placeId in addressData
        if (result.placeId) {
          addressData.value.placeId = result.placeId;
        }

        if (!placeDetails) {
          const text = result.text || '';
          addressQuery.value = text;

          const parts = text.split(',').map((p) => p.trim());
          if (parts.length > 0) {
            const streetPart = parts[0];
            const streetMatch = streetPart.match(/^(.+?)\s+(\d+[a-zA-Z]?)$/);
            if (streetMatch) {
              addressData.value.street = streetMatch[1];
              addressData.value.houseNumber = streetMatch[2];
            } else {
              addressData.value.street = streetPart;
            }
          }
          if (parts.length > 1) {
            addressData.value.city = parts[parts.length - 1] || '';
          }
          return;
        }

        if (placeDetails.addressComponents && placeDetails.addressComponents.length > 0) {
          const components = {
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            country: '',
          };

          placeDetails.addressComponents.forEach((component) => {
            const types = component.types || [];

            if (types.includes('street_number')) {
              components.houseNumber = component.longText || component.shortText || '';
            }
            if (types.includes('route')) {
              components.street = component.longText || component.shortText || '';
            }
            if (types.includes('postal_code')) {
              components.postalCode = component.longText || component.shortText || '';
            }
            if (types.includes('locality')) {
              if (!components.city) {
                components.city = component.longText || component.shortText || '';
              }
            } else if (types.includes('administrative_area_level_2') && !components.city) {
              components.city = component.longText || component.shortText || '';
            }
            if (types.includes('country')) {
              components.country = component.shortText || component.longText || '';
            }
          });

          if (components.street) {
            addressData.value.street = components.street;
            addressQuery.value = components.street;
          }
          if (components.houseNumber) {
            addressData.value.houseNumber = components.houseNumber;
          }
          if (components.postalCode) {
            addressData.value.postalCode = components.postalCode;
            postalCodeQuery.value = components.postalCode;
          }
          if (components.city) {
            addressData.value.city = components.city;
          }
          if (components.country) {
            addressData.value.country = components.country;
          }
        } else {
          const formatted = placeDetails.formattedAddress || result.text;
          if (formatted) {
            const parts = formatted.split(',').map((p) => p.trim());
            if (parts.length > 0) {
              const streetPart = parts[0];
              const streetMatch = streetPart.match(/^(.+?)\s+(\d+[a-zA-Z]?)$/);
              if (streetMatch) {
                addressData.value.street = streetMatch[1];
                addressData.value.houseNumber = streetMatch[2];
                addressQuery.value = streetMatch[1];
              } else {
                addressData.value.street = streetPart;
                addressQuery.value = streetPart;
              }
            }
            if (parts.length > 1) {
              addressData.value.city = parts[parts.length - 2] || '';
            }
            if (parts.length > 2) {
              const postalMatch = parts[parts.length - 2]?.match(/(\d{4}\s?[A-Z]{2})/);
              if (postalMatch) {
                addressData.value.postalCode = postalMatch[1].replace(/\s/g, '');
                postalCodeQuery.value = postalMatch[1].replace(/\s/g, '');
              }
            }
          }
        }
      };

      /**
       * PDOK API lookup for Dutch addresses (postcode + huisnummer)
       */
      const applyPdokResult = (result, postalCode, houseNumber, overwrite) => {
        if (overwrite || !addressData.value.street || addressData.value.street.trim() === '') {
          addressData.value.street = result.data.street || '';
          addressQuery.value = result.data.street || '';
        }
        if (overwrite || !addressData.value.city || addressData.value.city.trim() === '') {
          addressData.value.city = result.data.city || '';
        }
        addressData.value.postalCode = result.data.postalCode || postalCode;
        postalCodeQuery.value = result.data.postalCode || postalCode;
        addressData.value.houseNumber = result.data.houseNumber || houseNumber;
        addressData.value.country = result.data.country || 'NL';
        addressData.value.coordinates = result.data.coordinates || null;
      };

      const lookupPdokAddress = async (postalCode, houseNumber, overwrite = false) => {
        if (!props.enablePdokLookup || !postalCode || !houseNumber) {
          return 'skipped';
        }

        if (addressData.value.country && addressData.value.country.toLowerCase() !== 'nl') {
          return 'skipped';
        }

        try {
          const cleanPostalCode = postalCode.replace(/\s+/g, '').toUpperCase();
          const cleanHouseNumber = houseNumber.trim();

          if (cleanPostalCode.length !== 6) {
            return 'skipped';
          }

          const lookupUrl = '/api/address/lookup';

          const response = await fetch(
            `${lookupUrl}?postalCode=${encodeURIComponent(cleanPostalCode)}&houseNumber=${encodeURIComponent(cleanHouseNumber)}&country=NL`
          );

          if (!response.ok) {
            throw new Error('Address lookup request failed');
          }

          const result = await response.json();

          if (result.success && result.data) {
            lookupError.value = '';
            applyPdokResult(result, cleanPostalCode, cleanHouseNumber, overwrite);
            return 'ok';
          }
          return 'not_found';
        } catch (error) {
          console.warn('Address lookup failed:', error);
          return 'failed';
        }
      };

      const addressMessage = (key, fallback) => $t?.(key) || fallback;

      /**
       * Appointment re-check. Every click ends in the confirmed view or a message.
       */
      const checkExpandedAddress = async () => {
        if (!props.showAddressCheck || !props.enablePdokLookup || isCheckingAddress.value) return;

        if (isUnchangedExpandedAddress()) {
          lookupError.value = '';
          viewState.value = 'confirmed';
          return;
        }

        const country = (addressData.value.country || 'NL').trim().toUpperCase();
        if (country !== 'NL' && country !== 'NEDERLAND') {
          lookupError.value = addressMessage(
            'form.fields.address.check_nl_only',
            'Momenteel ondersteunen we alleen Nederlandse adressen.'
          );
          return;
        }

        const postalCode = addressData.value.postalCode || '';
        const houseNumber = (addressData.value.houseNumber || '').trim();
        const cleanPostalCode = postalCode.replace(/\s+/g, '').toUpperCase();
        if (!/^\d{4}[A-Z]{2}$/.test(cleanPostalCode)) {
          lookupError.value = addressMessage(
            'form.fields.address.postal_code_invalid',
            'Vul een geldige postcode in (bijv. 1234 AB)'
          );
          return;
        }
        if (!houseNumber) {
          lookupError.value = addressMessage(
            'form.fields.address.house_number_required',
            'Huisnummer is verplicht'
          );
          return;
        }

        isCheckingAddress.value = true;
        lookupError.value = '';
        try {
          const outcome = await lookupPdokAddress(postalCode, houseNumber, true);
          if (outcome === 'ok') {
            const street = (addressData.value.street || '').trim();
            const city = (addressData.value.city || '').trim();
            if (!street || !city) {
              lookupError.value = addressMessage(
                'form.fields.address.not_found',
                'Adres niet gevonden. Controleer postcode en huisnummer.'
              );
              return;
            }
            lookupError.value = '';
            viewState.value = 'confirmed';
            return;
          }
          if (outcome === 'failed') {
            lookupError.value = addressMessage(
              'form.fields.address.lookup_failed',
              'Adres opzoeken mislukt. Vul handmatig in.'
            );
            return;
          }
          lookupError.value = addressMessage(
            'form.fields.address.not_found',
            'Adres niet gevonden. Controleer postcode en huisnummer.'
          );
        } finally {
          isCheckingAddress.value = false;
        }
      };

      const handlePostalCodeBlur = () => {
        setTimeout(() => {
          if (activeSearchField.value === 'postalCode') {
            showAutocomplete.value = false;
            activeSearchField.value = null;
          }
        }, 200);

        validatePostalCodeFormat();

        // Appointments re-check only through the button. Other address fields
        // still fill an empty street on blur.
        if (
          props.enablePdokLookup &&
          !props.showAddressCheck &&
          addressData.value.postalCode &&
          addressData.value.houseNumber
        ) {
          lookupPdokAddress(addressData.value.postalCode, addressData.value.houseNumber);
        }
      };

      /**
       * Handle street input blur
       */
      const handleStreetBlur = () => {
        setTimeout(() => {
          if (activeSearchField.value === 'street') {
            showAutocomplete.value = false;
            activeSearchField.value = null;
          }
        }, 200);
      };

      // Watch for postal code + house number to auto-enrich address (for expanded view)
      watch(
        [() => addressData.value.postalCode, () => addressData.value.houseNumber],
        ([postalCode, houseNumber]) => {
          // Only for expanded view - smart mode handles this differently.
          // Appointments use the check button instead of this watcher.
          if (viewState.value !== 'expanded' || props.showAddressCheck) return;

          if (lookupTimeout.value) {
            clearTimeout(lookupTimeout.value);
          }

          if (
            props.enablePdokLookup &&
            postalCode &&
            houseNumber &&
            postalCode.replace(/\s+/g, '').length === 6 &&
            /^\d{4}[A-Z]{2}$/i.test(postalCode.replace(/\s+/g, '')) &&
            !addressData.value.street
          ) {
            lookupTimeout.value = setTimeout(() => {
              lookupPdokAddress(postalCode, houseNumber);
            }, 800);
          }
        }
      );

      watch(
        addressData,
        (newValue) => {
          if (JSON.stringify(newValue) !== JSON.stringify(props.modelValue)) {
            emit('update:modelValue', newValue);
          }
        },
        { deep: true }
      );

      watch(
        () => props.modelValue,
        (newValue) => {
          if (!newValue) return;
          // Parent echoes the same address. Replacing local state remounts the open form.
          if (JSON.stringify(newValue) === JSON.stringify(addressData.value)) return;

          addressData.value = { ...newValue };
          // Update query fields
          if (newValue.street) addressQuery.value = newValue.street;
          if (newValue.postalCode) postalCodeQuery.value = newValue.postalCode;

          // Update viewState for smart mode when we have complete address
          if (props.mode === 'smart') {
            const hasCompleteAddress =
              newValue.street && newValue.houseNumber && newValue.postalCode && newValue.city;
            if (hasCompleteAddress && viewState.value === 'compact') {
              viewState.value = 'confirmed';
            } else if (!hasCompleteAddress && viewState.value === 'confirmed') {
              viewState.value = 'compact';
            }
          }
        },
        { deep: true }
      );

      // Watch mode changes
      watch(
        () => props.mode,
        (newMode) => {
          const mode = newMode || 'smart';
          if (mode === 'full') {
            viewState.value = 'expanded';
          } else if (mode === 'compact') {
            viewState.value = 'compact';
          } else if (mode === 'smart') {
            // Re-evaluate viewState for smart mode
            const hasCompleteAddress =
              addressData.value.street &&
              addressData.value.houseNumber &&
              addressData.value.postalCode &&
              addressData.value.city;
            viewState.value = hasCompleteAddress ? 'confirmed' : 'compact';
          }
        }
      );

      // Verify viewState on mount
      onMounted(() => {
        const mode = props.mode || 'smart';
        if (mode === 'smart') {
          const hasCompleteAddress =
            addressData.value.street &&
            addressData.value.houseNumber &&
            addressData.value.postalCode &&
            addressData.value.city;
          if (hasCompleteAddress && viewState.value === 'compact') {
            viewState.value = 'confirmed';
          } else if (!hasCompleteAddress && viewState.value !== 'compact') {
            viewState.value = 'compact';
          }
        }
      });

      onUnmounted(() => {
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }
        if (lookupTimeout.value) {
          clearTimeout(lookupTimeout.value);
        }
        if (expandTimer) {
          clearTimeout(expandTimer);
        }
        if (abortController) {
          abortController.abort();
        }
      });

      return {
        addressData,
        addressQuery,
        postalCodeQuery,
        showAutocomplete,
        autocompleteResults,
        isLoading,
        isCheckingAddress,
        countries: COUNTRIES,
        streetInput,
        lookupError,
        viewState,
        activeSearchField,
        handlePostalCodeBlur,
        handlePostalCodeInput,
        handleStreetBlur,
        handleStreetInput,
        selectAutocompleteResult,
        expandForm,
        handleCompactPostalInput,
        handleCompactHouseNumberInput,
        handleCompactBlur,
        checkExpandedAddress,
        enablePdokLookup: props.enablePdokLookup,
        showAddressCheck: props.showAddressCheck,
      };
    },
  };
</script>

<style scoped lang="scss">
  .address {
    // Compact view styles
    &__compact {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__compact-row {
      display: flex;
      gap: 0.5rem;
    }

    &__compact-loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      color: var(--cs-text-body, #6b7280);
      font-size: 0.875rem;
    }

    // Confirmed view styles
    &__confirmed {
      margin-top: 0.25rem;
    }

    &__preview {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      // Primary brand colour, so the confirmed address matches the theme's main colour
      border: 1px solid var(--cs-brand-primary);
      border-radius: var(--cs-border-radius, 0.5rem);
      background-color: var(--cs-brand-primary-light);
      color: var(--cs-choice-active-text);
      transition: all 150ms ease-in-out;
    }

    &__preview-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      color: var(--cs-brand-primary);
      flex-shrink: 0;
      margin-top: 0.125rem;
    }

    &__preview-text {
      flex: 1;
      min-width: 0;
    }

    &__preview-line1 {
      font-weight: 500;
      color: var(--cs-text-body, #111827);
      font-size: 0.9375rem;
    }

    &__preview-line2 {
      color: var(--cs-text-body, #6b7280);
      font-size: 0.875rem;
      margin-top: 0.125rem;
    }

    &__check-btn {
      align-self: flex-start;
      margin-top: 0.25rem;
      border-radius: var(--cs-border-radius, 0.5rem);
    }

    &__edit-btn {
      // padding: 0.375rem 0.75rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--cs-brand-primary, #3b82f6);
      background: transparent;
      border: 1px solid transparent;
      border-color: transparent;
      border-radius: var(--cs-border-radius, 0.375rem);
      cursor: pointer;
      transition: all 0.15s ease;
      white-space: nowrap;

      &:hover {
        text-decoration: underline;
      }
    }

    // Expanded/full view styles
    &__fields {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__field {
      width: 100%;
      position: relative;

      &--number {
        width: 100px;
        flex-shrink: 0;
      }

      &--number-compact {
        flex: 1;
        min-width: 100px;
      }

      &--postal {
        width: 120px;
        flex-shrink: 0;
      }

      &--postal-compact {
        flex: 1.2;
        min-width: 140px;
      }

      &--city {
        flex: 1;
        min-width: 0;
      }

      &--country {
        width: 180px;
        flex-shrink: 0;
      }
    }

    &__field-row {
      display: flex;
      gap: 0.5rem;
      @include lg-down {
        flex-direction: column;
      }
      &--no-break {
        @include lg-down {
          flex-direction: row;
        }
      }
    }

    &__error {
      color: var(--cs-error, #dc2626);
      font-size: 0.75rem;
      margin-top: 0.25rem;
    }

    &__autocomplete {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: var(--cs-input-default-background, white);
      border: 1px solid var(--cs-input-default-border, #e5e7eb);
      border-top: none;
      border-radius: 0 0 var(--cs-border-radius, 0.375rem) var(--cs-border-radius, 0.375rem);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      max-height: 300px;
      overflow-y: auto;
      z-index: 2000;
      margin-top: 0;
    }

    &__autocomplete-loading {
      padding: 1rem;
      text-align: center;
      color: var(--cs-text-body, #6b7280);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    &__autocomplete-spinner {
      width: 1rem;
      height: 1rem;
      border: 2px solid var(--cs-input-default-border, #e5e7eb);
      border-top: 2px solid var(--cs-brand-primary, #3b82f6);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    &__autocomplete-item {
      padding: 0.75rem 1rem;
      cursor: pointer;
      border-bottom: 1px solid var(--cs-input-default-border, #f3f4f6);
      transition: background-color 0.15s ease;

      &:hover {
        background-color: var(--cs-background-primary, #f9fafb);
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &__autocomplete-name {
      font-weight: 500;
      color: var(--cs-text-body, #111827);
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
    }

    &__autocomplete-address {
      font-size: 0.75rem;
      color: var(--cs-text-body, #6b7280);
      opacity: 0.8;
    }

    &__autocomplete-no-results {
      padding: 1rem;
      text-align: center;
      color: var(--cs-text-body, #6b7280);
      font-size: 0.875rem;
      font-style: italic;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
